/**
 * Mandate Bridge Service
 *
 * Connects Mandate transition governance to platform Individual Space primitives:
 * personal collaboration spaces, prioritization contexts (P01), items, decisions,
 * and isolated sessions. All platform writes tag origin_module = 'mandate'.
 *
 * Canon: 00-build-contract §2.3, 10A Config 9 (Personal or Team Focus),
 *        10C personal context invariant.
 */

import { getSupabaseClient, isSupabaseConfigured } from './supabaseClient';
import { getCurrentWorkspaceId } from './supabaseSync';
import { ensurePersonalSpace } from './spaceService';
import { useItemsStore } from '../stores/prioritization/itemsStore';
import { useDecisionsStore } from '../stores/prioritization/decisionsStore';
import { createSessionWithFeedback } from './sessions/createSessionFlow';
import { useContextsStore } from '../stores/prioritization/contextsStore';
import { useSpaceStore } from '../stores/prioritization/spaceStore';
import { useTenantStore } from '../stores/prioritization/tenantStore';
import { clearSpaceMembershipCache } from './permissionResolver';
import type {
  MandateTransition,
  MandatePriority,
  MandateDecision,
  MandateStakeholder,
} from '../types/mandate';
import type { OriginModule } from '../types/prioritization/originModule';
import type { Item } from '../types/prioritization/lifecycle';

const MANDATE_ORIGIN: OriginModule = 'mandate';

export interface BootstrapMandateSpaceResult {
  spaceId: string | null;
  contextId: string | null;
  /** The workspace the Individual Space lives in (the transition's). */
  workspaceId?: string;
  error?: string;
}

/** Ensure mandate transition has a dedicated personal space + personal context. */
export async function bootstrapMandateIndividualSpace(
  transition: MandateTransition,
  userId: string
): Promise<BootstrapMandateSpaceResult> {
  const workspaceId = transition.workspaceId ?? getCurrentWorkspaceId();
  if (!workspaceId) {
    return { spaceId: null, contextId: null, error: 'No workspace context' };
  }

  let spaceId = transition.spaceId ?? null;
  let contextId = transition.contextId ?? null;

  // Always run the RPC — creates the space when missing and repairs owner membership.
  if (isSupabaseConfigured()) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('ensure_mandate_space', {
      p_workspace_id: workspaceId,
      p_transition_id: transition.id,
      p_space_name: `Mandate — ${transition.title || transition.role}`,
    });

    if (error) {
      console.error('[MandateBridge] ensure_mandate_space error:', error.message);
      return { spaceId: null, contextId: null, error: error.message };
    }

    const result = data as { success: boolean; space_id?: string; error?: string };
    if (!result.success) {
      return { spaceId: null, contextId: null, error: result.error ?? 'ensure_mandate_space failed' };
    }
    spaceId = result.space_id ?? spaceId;
  }

  if (!contextId) {
    const context = await useContextsStore.getState().createContext({
      workspace_id: workspaceId,
      context_type: 'personal',
      owner_id: userId,
      name: `Mandate: ${transition.title || transition.role}`,
      actor_id: userId,
    });
    contextId = context?.id ?? null;

    if (contextId && isSupabaseConfigured()) {
      const supabase = getSupabaseClient();
      await supabase
        .from('mandate_transitions')
        .update({ context_id: contextId, updated_at: new Date().toISOString() })
        .eq('id', transition.id);
    }
  }

  if (spaceId) {
    // The Individual Space lives in the transition's workspace. A person who
    // belongs to several can arrive with another one current; every store reads
    // by the current workspace, so the shell would show that workspace's items
    // and sessions under the Mandate chrome (staging, 2026-11-02: "0 sessions").
    // Make the transition's workspace current before selecting its space.
    const tenant = useTenantStore.getState();
    if (tenant.currentWorkspaceId !== workspaceId) {
      tenant.setCurrentWorkspace(workspaceId);
    }
    clearSpaceMembershipCache(spaceId);
    await useSpaceStore.getState().fetchSpaces(workspaceId);
    useSpaceStore.getState().setCurrentSpace(spaceId);
  }

  return { spaceId, contextId, workspaceId };
}

/** Activate the mandate space when entering the Mandate module. */
export async function activateMandateSpace(transition: MandateTransition): Promise<void> {
  const workspaceId = transition.workspaceId ?? getCurrentWorkspaceId();
  if (!workspaceId) return;

  await bootstrapMandateIndividualSpace(transition, transition.ownerId);
}

/** Promote a mandate priority to a workspace item (origin_module = mandate). */
export async function promoteMandatePriorityToItem(
  priority: MandatePriority,
  transition: MandateTransition,
  userId: string
): Promise<{ itemId: string | null; error?: string }> {
  if (priority.platformItemId) {
    return { itemId: priority.platformItemId };
  }

  const workspaceId = transition.workspaceId ?? getCurrentWorkspaceId();
  if (!workspaceId) return { itemId: null, error: 'No workspace' };

  let spaceId = transition.spaceId;
  if (!spaceId) {
    const boot = await bootstrapMandateIndividualSpace(transition, userId);
    spaceId = boot.spaceId ?? undefined;
  }

  const item = useItemsStore.getState().addCandidate({
    title: priority.title,
    description: priority.description ?? priority.rationale ?? undefined,
    source: 'mandate',
    createdBy: userId,
    metadata: {
      mandateTransitionId: transition.id,
      mandatePriorityId: priority.id,
      mandateRank: priority.rank,
    },
  });

  if (!item) return { itemId: null, error: 'Failed to create item' };

  const enriched: Item = {
    ...item,
    workspaceId,
    spaceId: spaceId ?? null,
    ...( { originModule: MANDATE_ORIGIN } as Partial<Item> ),
  };

  const syncResult = await useItemsStore.getState().syncToSupabase({
    ...enriched,
    originModule: MANDATE_ORIGIN,
  } as Item & { originModule: OriginModule });

  if (!syncResult.success) {
    return { itemId: null, error: syncResult.error ?? 'Item sync failed' };
  }

  if (isSupabaseConfigured()) {
    const supabase = getSupabaseClient();
    await supabase
      .from('mandate_priorities')
      .update({ platform_item_id: item.id, updated_at: new Date().toISOString() })
      .eq('id', priority.id);
  }

  return { itemId: item.id };
}

/** Mirror a mandate decision into the platform decisions table. */
export async function syncMandateDecisionToPlatform(
  decision: MandateDecision,
  transition: MandateTransition
): Promise<{ platformDecisionId: string | null; error?: string }> {
  if (decision.platformDecisionId) {
    return { platformDecisionId: decision.platformDecisionId };
  }

  const platformDecision = await useDecisionsStore.getState().createDecision({
    title: decision.title,
    body: [decision.description, decision.outcome].filter(Boolean).join('\n\n') || undefined,
    decisionKind: 'manual',
    source: 'mandate_transition',
    tags: ['mandate', `transition:${transition.id}`],
    rationaleContext: decision.signal ?? undefined,
    rationaleReasoning: decision.rationale,
    rationaleAlternatives: decision.peopleConsulted ?? undefined,
    originModule: MANDATE_ORIGIN,
    relatedObjects: [],
  });

  if (!platformDecision) {
    return { platformDecisionId: null, error: 'Failed to create platform decision' };
  }

  if (isSupabaseConfigured()) {
    const supabase = getSupabaseClient();
    await supabase
      .from('mandate_decisions')
      .update({
        platform_decision_id: platformDecision.id,
        updated_at: new Date().toISOString(),
      })
      .eq('id', decision.id);
  }

  return { platformDecisionId: platformDecision.id };
}

/** Promote stakeholder to a people-type item in the mandate space. */
export async function promoteMandateStakeholderToItem(
  stakeholder: MandateStakeholder,
  transition: MandateTransition,
  userId: string
): Promise<{ itemId: string | null; error?: string }> {
  if (stakeholder.platformItemId) {
    return { itemId: stakeholder.platformItemId };
  }

  const workspaceId = transition.workspaceId ?? getCurrentWorkspaceId();
  if (!workspaceId) return { itemId: null, error: 'No workspace' };

  let spaceId = transition.spaceId;
  if (!spaceId) {
    const boot = await bootstrapMandateIndividualSpace(transition, userId);
    spaceId = boot.spaceId ?? undefined;
  }

  const description = [
    stakeholder.role,
    stakeholder.influence && `Influence: ${stakeholder.influence}`,
    stakeholder.stance && `Stance: ${stakeholder.stance}`,
    stakeholder.trustGaps,
    stakeholder.notes,
  ]
    .filter(Boolean)
    .join('\n');

  const item = useItemsStore.getState().addCandidate({
    title: stakeholder.name,
    description: description || undefined,
    source: 'mandate',
    createdBy: userId,
    metadata: {
      mandateTransitionId: transition.id,
      mandateStakeholderId: stakeholder.id,
      objectType: 'people',
      influence: stakeholder.influence,
      stance: stakeholder.stance,
    },
  });

  if (!item) return { itemId: null, error: 'Failed to create item' };

  const syncResult = await useItemsStore.getState().syncToSupabase({
    ...item,
    workspaceId,
    spaceId: spaceId ?? null,
    originModule: MANDATE_ORIGIN,
  } as Item & { originModule: OriginModule });

  if (!syncResult.success) {
    return { itemId: null, error: syncResult.error ?? 'Item sync failed' };
  }

  if (isSupabaseConfigured()) {
    const supabase = getSupabaseClient();
    await supabase
      .from('mandate_stakeholders')
      .update({ platform_item_id: item.id })
      .eq('id', stakeholder.id);
  }

  return { itemId: item.id };
}

/** Create an isolated stack-ranking session from active mandate priorities. */
export async function createMandateStackRankingSession(
  priorities: MandatePriority[],
  transition: MandateTransition,
  userId: string
): Promise<{ sessionId: string | null; error?: string }> {
  const active = priorities.filter((p) => p.status === 'active');
  if (active.length < 2) {
    return { sessionId: null, error: 'Need at least two active priorities' };
  }

  let contextId = transition.contextId;
  if (!contextId) {
    const boot = await bootstrapMandateIndividualSpace(transition, userId);
    contextId = boot.contextId ?? undefined;
  }

  const itemIds: string[] = [];
  for (const p of active) {
    const { itemId, error } = await promoteMandatePriorityToItem(p, transition, userId);
    if (!itemId) {
      return { sessionId: null, error: error ?? `Failed to promote priority: ${p.title}` };
    }
    itemIds.push(itemId);
  }

  const result = await createSessionWithFeedback({
    name: `Mandate stack — Day ${transition.startDate ? new Date(transition.startDate).toLocaleDateString() : '100'}`,
    facilitatorId: userId,
    createdBy: userId,
    sessionContextMode: 'isolated',
    intent: 'rank_priorities',
    itemIds,
    selectedToolId: 'stack_ranking',
    contextId: contextId ?? undefined,
    workspaceId: transition.workspaceId,
    originModule: MANDATE_ORIGIN,
  });

  if (!result.ok) {
    return { sessionId: null, error: result.error ?? 'Failed to create session' };
  }

  return { sessionId: result.sessionId! };
}

/** Fallback: use default personal space if mandate space RPC unavailable. */
export async function fallbackPersonalSpace(workspaceId: string): Promise<string | null> {
  return ensurePersonalSpace(workspaceId);
}
